import React, { useContext, useEffect } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { IAppDispatch, store } from '@app/store';
import AppNotification from '@components/app-notification';
import { SnackbarProvider } from 'notistack';
import { ThemeProvider } from '@emotion/react';
import theme from '@app/services/theme';
import authConfig from '@app/configs/auth-config';
import { AuthContext, AuthProvider } from 'react-oauth2-code-pkce';
import {
  selectSessionExpired,
  selectToken,
  setIsSupportUser,
  setSessionExpired,
  setToken,
} from '@store/features/auth';
import {
  HistoryRouterProps,
  Route,
  Routes,
  unstable_HistoryRouter as HistoryRouter,
} from 'react-router-dom';
import history from '@app/services/history';

import CustomerRouter from '@features/workspace/routes';
import { OverallLoading } from '@components/overall-loading';
import Loader from '@components/loader';
import {
  selectIsAccessDenied,
  selectIsAuthFailed,
  selectIsInitializing,
  selectIsNoCompanyFound,
  selectIsNoUserFound,
  selectIsNoUserIdResolved,
  selectIsZafClientFailed,
  setIsAuthFailed,
  setIsInitializing,
  setIsZafClientFailed,
} from '@store/features/loading';
import useResolvePersonId from '@features/workspace/hooks/use-resolve-person-id';
import PageNotFound from '@features/workspace/components/errors/page-not-found';
import RequestAuth from '@features/workspace/components/errors/request-auth';
import CompanyNotFound from '@features/workspace/components/errors/company-not-found';
import AccessDenied from '@features/workspace/components/errors/access-denied';
import ZafClientFailed from '@features/workspace/components/errors/zaf-client-failed';
import UserNotResolved from '@features/workspace/components/errors/user-not-resolved';
import UserNotFound from '@features/workspace/components/errors/user-not-found';
import { setActiveCompanyId } from '@features/workspace/store';
import { useCompanyWatcher } from '@features/workspace/store/actions/init';
import { QueryClientProvider } from 'react-query';
import { queryClient } from '@app/services/react-query';
import 'react-toastify/dist/ReactToastify.min.css';
import { ToastContainer } from 'react-toastify';
import logger from '@app/services/logger';
import { zafClient } from '@app/services/zendesk-sdk';
import { isIframe } from '@utils/iframe';
import {
  applyAuthRelayPayload,
  isAuthPopupWindow,
  isAuthRelayMessage,
} from '@utils/auth-relay';
import {
  canTrySilentSignIn,
  isSignInRequiredResponse,
  markSilentSignInTried,
  rememberPageBeforeSignIn,
  takePageBeforeSignIn,
} from '@utils/sign-in-redirect';

const params = new URLSearchParams(document.location.search);
const personIdParam = params.get('personId');
const activeCompanyIdParam = params.get('activeCompanyId');
const isSupportUserParam = params.get('isSupport');
// Keycloak had no session for a silent sign-in: expected, not a failure
const isSignInRequired = isSignInRequiredResponse(document.location.search);

const AppRouter: React.FC = () => {
  if (personIdParam) {
    sessionStorage.setItem('active-person-id', personIdParam);
  }

  const dispatch = useDispatch<IAppDispatch>();
  const {
    token,
    error: authError,
    logOut,
    login,
    loginInProgress,
  } = useContext(AuthContext);
  const reduxToken = useSelector(selectToken);
  const sessionExpired = useSelector(selectSessionExpired);

  useEffect(() => {
    if (isIframe && !zafClient) {
      logger.debug('AppRouter: ZAFClient not available in iframe mode');
      dispatch(setIsZafClientFailed(true));
      dispatch(setIsInitializing(false));
    }
  }, [dispatch]);

  // autoLogin is disabled app-wide (it would navigate the Zendesk iframe itself
  // and break the ZAF SDK handshake). Top-level windows sign in by redirect:
  // - the sign-in popup runs a normal login (the agent clicked Sign In);
  // - any other page, such as "Manage company", only checks for an existing
  //   Keycloak session (prompt=none). With one, the agent is let straight in.
  //   Without one, Keycloak answers login_required rather than going to Entra,
  //   and the Sign In screen asks the agent to click.
  // Each window gets its own tokens, so it never races the widget's renewals.
  useEffect(() => {
    if (isIframe || token || loginInProgress || authError) return;
    if (isAuthPopupWindow()) {
      login();

      return;
    }
    if (!canTrySilentSignIn()) {
      dispatch(setIsInitializing(false));

      return;
    }
    markSilentSignInTried();
    rememberPageBeforeSignIn();
    login(undefined, { prompt: 'none' });
  }, [token, loginInProgress, authError, login, dispatch]);

  // Back from a top-level sign-in: the redirect URI is the site root, so reload
  // the page the agent asked for to apply its query parameters again. Wait for
  // loginInProgress to clear, or the reloaded page reports a bad login state.
  useEffect(() => {
    if (!token || loginInProgress || isAuthPopupWindow()) return;
    const urlBeforeAuth = takePageBeforeSignIn();

    if (urlBeforeAuth) window.location.replace(urlBeforeAuth);
  }, [token, loginInProgress]);

  // In the widget, not authenticated and nothing in progress: stop showing the
  // loading screen so RequestAuth's "Sign In" button becomes available
  useEffect(() => {
    if (isIframe && !token && !loginInProgress) {
      dispatch(setIsInitializing(false));
    }
  }, [token, loginInProgress, dispatch]);

  // Running as the sign-in popup, the tokens are handed back to the opener by
  // authConfig.postLogin, only after a fresh login (see auth-config.ts)

  // Session can't be renewed, or the API rejected the token: clear the stored
  // session (no logoutEndpoint is configured, so logOut() doesn't navigate) and
  // let the Sign In screen render. Never call login() here - it would navigate
  // the Zendesk iframe itself.
  useEffect(() => {
    if (!sessionExpired) return;
    logOut();
    dispatch(setToken(null));
    dispatch(setSessionExpired(false));
  }, [sessionExpired, logOut, dispatch]);

  // Running as the opener: pick up tokens relayed from the popup in place
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verify the origin before trusting the payload
      if (event.origin !== window.location.origin) return;
      if (!isAuthRelayMessage(event)) return;
      applyAuthRelayPayload(event.data.payload);
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, []);

  if (isSupportUserParam !== null) {
    dispatch(setIsSupportUser(isSupportUserParam === 'true'));
  }

  if (activeCompanyIdParam) {
    dispatch(setActiveCompanyId(activeCompanyIdParam));
  }

  useResolvePersonId(personIdParam);
  useCompanyWatcher();

  const isInitializing = useSelector(selectIsInitializing);
  const isNoPersonResolved = useSelector(selectIsNoUserIdResolved);
  const isNoPersonFound = useSelector(selectIsNoUserFound);
  const isNoCompanyFound = useSelector(selectIsNoCompanyFound);
  const isAccessDenied = useSelector(selectIsAccessDenied);
  const isAuthFailed = useSelector(selectIsAuthFailed);
  const isZafClientFailed = useSelector(selectIsZafClientFailed);

  // Auth failed
  if (authError) {
    if (isSignInRequired) {
      logger.info(`Auth: no Keycloak session, showing Sign In (${authError})`);
    } else {
      logger.error(new Error(`AuthContext Error: ${authError}`), authError);
    }
    dispatch(setIsAuthFailed(true));
    dispatch(setIsInitializing(false));
  }

  useEffect(() => {
    if (!!token && token === reduxToken) return;
    // If token regenerated - store to redux
    if (!!token && token !== reduxToken) {
      const reduxTokenBefore = reduxToken ? `${reduxToken}` : null;

      dispatch(setToken(token));
      if (!reduxTokenBefore) {
        const pathname = sessionStorage.getItem('path-before-auth');

        // Preserve Zendesk's origin/app_guid query params - ZAFClient.init() requires them
        history.push({
          pathname: pathname || '/',
          search: window.location.search,
        });
      }
    }
  }, [token, reduxToken, dispatch]);

  if (isInitializing || sessionExpired) {
    return <OverallLoading />;
  }

  // RequestAuth is now the normal "please sign in" screen (not just an error
  // page) - it shows whenever we simply don't have a token yet, as well as
  // when a real login error occurred.
  if (!isInitializing && (isAuthFailed || !token)) {
    return <RequestAuth />;
  }

  if (!isInitializing && isAccessDenied) {
    return <AccessDenied />;
  }

  if (!isInitializing && isNoPersonResolved) {
    return <UserNotResolved />;
  }

  if (!isInitializing && isNoPersonFound) {
    return <UserNotFound />;
  }

  if (!isInitializing && isNoCompanyFound) {
    return <CompanyNotFound />;
  }

  if (!isInitializing && isZafClientFailed) {
    return <ZafClientFailed />;
  }

  return (
    <HistoryRouter
      history={history as unknown as HistoryRouterProps['history']}
    >
      <Routes>
        <Route element={<CustomerRouter />} path="/customer/*" />
        <Route element={<CustomerRouter />} path="/lead/*" />
        <Route element={<PageNotFound />} path="*" />
      </Routes>
    </HistoryRouter>
  );
};

const Application: React.FC = () => {
  if (history.location.pathname !== '/') {
    logger.debug(`Saving post auth redirect uri: ${history.location.pathname}`);
    sessionStorage.setItem('path-before-auth', history.location.pathname);
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <>
          <AuthProvider authConfig={authConfig}>
            <SnackbarProvider
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
            >
              <ThemeProvider theme={theme}>
                <>
                  <AppNotification />
                  <AppRouter />
                  <ToastContainer />
                </>
              </ThemeProvider>
            </SnackbarProvider>
          </AuthProvider>
          <Loader />
        </>
      </Provider>
    </QueryClientProvider>
  );
};

export default Application;
